import { createRoute } from "@tanstack/react-router";
import { appAuthRoute } from "./index";
import { ENTITY } from "../../constants";
import { checkPermissionsAndRedirect } from "../../helpers/checkPermissions";
import { audienceViewLoader, audienceFormLoader } from "../loaders/audience";
import { AudienceBreadcrumb } from "../../breadcrumbs/audience";
import Spinner from "../../components/general/Spinner";
// import loading pages
import EntityListLoading from "../../pages/entities/loading/EntityListLoading";
import EntityViewLoading from "../../pages/entities/loading/EntityViewLoading";
import EntityEditLoading from "../../pages/entities/loading/EntityEditLoading";

const { AUDIENCE } = ENTITY;

export const audienceRouteFactory = (parent = appAuthRoute) => {
  // If parent route is appAuthRoute => the route is NOT nested (ie. its directly under appAuthRoute)
  // If parent route is NOT appAuthRoute => route is nested
  const isNestedRoute = parent !== appAuthRoute;

  // Root

  const audienceRoute = createRoute({
    getParentRoute: () => parent,
    path: AUDIENCE.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: AUDIENCE.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: AUDIENCE.label.upFirstPlural,
    },
  });

  // List audiences

  const audienceListRoute = createRoute({
    getParentRoute: () => audienceRoute,
    path: "/",
    pendingComponent: () => <EntityListLoading entity={AUDIENCE} />,
  }).lazy(() => import("./audience.lazy").then((d) => d.AudienceListRoute));

  // View audience

  const audienceViewRootRoute = createRoute({
    getParentRoute: () => audienceRoute,
    path: "$audienceId",
    staticData: {
      breadcrumb: AudienceBreadcrumb,
    },
    loader: audienceViewLoader,
  });

  // Edit audience

  const audienceEditRoute = createRoute({
    getParentRoute: () => audienceViewRootRoute,
    path: "edit",
    staticData: {
      breadcrumb: "Edit",
    },
    loader: audienceFormLoader,
    pendingComponent: () => <EntityEditLoading entity={AUDIENCE} />,
  }).lazy(() => import("./audience.lazy").then((d) => d.AudienceEditRoute));

  // View audience Layout

  const audienceViewLayoutRoute = createRoute({
    getParentRoute: () => audienceViewRootRoute,
    id: "audience-view-layout",
    pendingComponent: () => <EntityViewLoading entity={AUDIENCE} />,
  }).lazy(() =>
    import("./audience.lazy").then((d) => d.AudienceViewLayoutRoute)
  );

  // View audience - General Tab

  const audienceViewLayoutGeneralTabRoute = createRoute({
    getParentRoute: () => audienceViewLayoutRoute,
    path: "/",
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./audience.lazy").then((d) => d.AudienceViewLayoutGeneralTabRoute)
  );

  // View audience - About Tab

  const audienceViewLayoutAboutTabRoute = createRoute({
    getParentRoute: () => audienceViewLayoutRoute,
    path: "about",
    staticData: {
      breadcrumb: "About",
    },
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./audience.lazy").then((d) => d.AudienceViewLayoutAboutTabRoute)
  );

  // Route tree
  audienceRoute.addChildren([
    // if the route is nested within a parent route, we do not provide ListRoute
    // because the route is already nested in a TabListRoute
    ...(isNestedRoute ? [] : [audienceListRoute]),
    audienceViewRootRoute.addChildren([
      audienceEditRoute,
      audienceViewLayoutRoute.addChildren([
        audienceViewLayoutGeneralTabRoute,
        audienceViewLayoutAboutTabRoute,
      ]),
    ]),
  ]);

  return audienceRoute;
};

const audienceRoute = audienceRouteFactory();

export default audienceRoute;
